import { Building2, Users, FileText, Settings, UserPlus, Edit, Plus, BarChart3, TrendingUp, AlertCircle, RefreshCw, FileCode, ImageIcon, Video, Upload } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useCompanyStats, useCompanyContent } from '@/hooks'
import { ContentQuickActions } from '@/components/dashboard/ContentQuickActions'
import { ContentItem } from '@/hooks/useCompanyContent'
import { CompanyAnalytics } from '@/components/analytics/CompanyAnalytics'
import { CARD_CLASSES, DASHBOARD_CONTAINER_CLASSES, DASHBOARD_SPACING } from '@/lib/constants/spacing'

interface CompanyAdminDashboardProps {
  userName?: string
  companyName?: string
}

export function CompanyAdminDashboard({ userName = 'Admin', companyName = 'Your Company' }: CompanyAdminDashboardProps) {
  const navigate = useNavigate()
  const { stats: companyStats, loading: statsLoading, error: statsError, refetch: refetchStats } = useCompanyStats()
  const { content: companyContent, loading: contentLoading, error: contentError } = useCompanyContent()

  // Calculate stats from real API data
  const dashboardStats = [
    {
      title: 'Total Users',
      value: companyStats?.userCount ?? 0,
      change: 'Team members',
      icon: <Users className="w-5 h-5" />,
      color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      trend: 'up' as const
    },
    {
      title: 'Content Items',
      value: companyStats?.totalContent ?? 0,
      change: 'Total resources',
      icon: <FileText className="w-5 h-5" />,
      color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
      trend: 'up' as const
    },
    {
      title: 'Presentations',
      value: companyStats?.presentationCount ?? 0,
      change: 'Slide decks',
      icon: <FileText className="w-5 h-5" />,
      color: 'bg-green-500/10 text-green-600 dark:text-green-400',
      trend: 'up' as const
    },
    {
      title: 'Documents',
      value: (companyStats?.pdfCount ?? 0) + (companyStats?.imageCount ?? 0) + (companyStats?.videoCount ?? 0),
      change: 'PDFs, images, videos',
      icon: <BarChart3 className="w-5 h-5" />,
      color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
      trend: 'stable' as const
    }
  ]

  const recentUsers = [
    { id: 1, name: 'John Smith', email: 'john@company.com', role: 'company_user', status: 'active', joined: '2025-01-28' },
    { id: 2, name: 'Sarah Johnson', email: 'sarah@company.com', role: 'company_user', status: 'active', joined: '2025-01-25' },
    { id: 3, name: 'Mike Williams', email: 'mike@company.com', role: 'company_user', status: 'pending', joined: '2025-01-22' }
  ]

  const quickActions = [
    {
      title: 'Invite User',
      description: 'Send invitation to new team members',
      icon: <UserPlus className="w-5 h-5" />,
      onClick: () => navigate('/users'),
      color: 'bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground'
    },
    {
      title: 'Edit Company Profile',
      description: 'Update company information and settings',
      icon: <Edit className="w-5 h-5" />,
      onClick: () => navigate('/company/profile'),
      color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500 hover:text-white'
    },
    {
      title: 'Upload Content',
      description: 'Add presentations, PDFs, and media',
      icon: <Plus className="w-5 h-5" />,
      onClick: () => navigate('/content'),
      color: 'bg-green-500/10 text-green-600 dark:text-green-400 hover:bg-green-500 hover:text-white'
    },
    {
      title: 'Manage Settings',
      description: 'Configure company preferences',
      icon: <Settings className="w-5 h-5" />,
      onClick: () => navigate('/settings'),
      color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:bg-purple-500 hover:text-white'
    }
  ]

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'company_admin':
        return 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
      case 'company_user':
        return 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
      default:
        return 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/10 text-green-600 dark:text-green-400'
      case 'pending':
        return 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400'
      default:
        return 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
    }
  }

  // Content action handlers
  const handleViewContent = (contentItem: ContentItem) => {
    navigate(`/content/${contentItem.id}`)
  }

  const handleEditContent = (contentItem: ContentItem) => {
    navigate(`/content/${contentItem.id}/edit`)
  }

  const handleDeleteContent = (contentItem: ContentItem) => {
    // TODO: Implement delete functionality
    console.log('Delete content:', contentItem.id)
  }

  const handleDownloadContent = (contentItem: ContentItem) => {
    // Download the content file
    window.open(contentItem.filePath, '_blank')
  }

  // Calculate content statistics
  const contentStats = [
    {
      title: 'Presentations',
      value: companyStats?.presentationCount ?? 0,
      icon: <FileText className="w-5 h-5" />,
      color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
    },
    {
      title: 'PDFs',
      value: companyStats?.pdfCount ?? 0,
      icon: <FileCode className="w-5 h-5" />,
      color: 'bg-red-500/10 text-red-600 dark:text-red-400'
    },
    {
      title: 'Images',
      value: companyStats?.imageCount ?? 0,
      icon: <ImageIcon className="w-5 h-5" />,
      color: 'bg-green-500/10 text-green-600 dark:text-green-400'
    },
    {
      title: 'Videos',
      value: companyStats?.videoCount ?? 0,
      icon: <Video className="w-5 h-5" />,
      color: 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
    }
  ]

  return (
    <div className={DASHBOARD_CONTAINER_CLASSES}>
      {/* Statistics Cards */}
      {statsLoading ? (
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 ${DASHBOARD_SPACING.GRID_GAP}`}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className={`${CARD_CLASSES} ${DASHBOARD_SPACING.CARD_PADDING} animate-pulse`}>
              <div className="flex items-start justify-between mb-4">
                <div className={`${DASHBOARD_SPACING.ICON_PADDING} bg-muted rounded-lg w-10 h-10`} />
              </div>
              <div className={DASHBOARD_SPACING.STAT_LABEL_GAP}>
                <div className="h-7 bg-muted rounded w-16 mb-2" />
                <div className="h-4 bg-muted rounded w-24" />
              </div>
              <div className="h-3 bg-muted rounded w-20" />
            </div>
          ))}
        </div>
      ) : statsError ? (
        <div className={`${CARD_CLASSES} border-destructive/50 ${DASHBOARD_SPACING.CARD_PADDING_COMPACT}`}>
          <div className={`flex items-center ${DASHBOARD_SPACING.LIST_ITEM_GAP}`}>
            <div className={`${DASHBOARD_SPACING.ICON_PADDING} bg-destructive/10 rounded-lg`}>
              <AlertCircle className="w-6 h-6 text-destructive" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground mb-1">Failed to load statistics</h3>
              <p className="text-sm text-muted-foreground">{statsError}</p>
            </div>
            <button
              onClick={() => refetchStats()}
              className={`${DASHBOARD_SPACING.BUTTON_PADDING} bg-primary text-primary-foreground rounded-md text-sm font-medium transition-colors hover:bg-primary/90 flex items-center ${DASHBOARD_SPACING.GAP_TIGHT}`}
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        </div>
      ) : (
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 ${DASHBOARD_SPACING.GRID_GAP}`}>
          {dashboardStats.map((stat, index) => (
            <div key={index} className={`${CARD_CLASSES} ${DASHBOARD_SPACING.CARD_PADDING}`}>
              <div className="flex items-start justify-between mb-4">
                <div className={`${DASHBOARD_SPACING.ICON_PADDING} ${stat.color} rounded-lg`}>
                  {stat.icon}
                </div>
                {stat.trend === 'up' && (
                  <div className={`flex items-center ${DASHBOARD_SPACING.GAP_TIGHT} text-green-600 dark:text-green-400`}>
                    <TrendingUp className="w-4 h-4" />
                  </div>
                )}
              </div>
              <div className={DASHBOARD_SPACING.STAT_LABEL_GAP}>
                <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.title}</p>
              </div>
              <p className="text-xs text-muted-foreground">{stat.change}</p>
            </div>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div className={`${CARD_CLASSES} ${DASHBOARD_SPACING.CARD_PADDING}`}>
        <div className={`flex items-center ${DASHBOARD_SPACING.ICON_TEXT_GAP} ${DASHBOARD_SPACING.HEADER_MARGIN_BOTTOM}`}>
          <div className={`${DASHBOARD_SPACING.ICON_PADDING} bg-primary/10 rounded-lg`}>
            <Settings className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Quick Actions</h2>
            <p className="text-sm text-muted-foreground">
              Common administrative tasks
            </p>
          </div>
        </div>

        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 ${DASHBOARD_SPACING.GRID_GAP}`}>
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              className={`${DASHBOARD_SPACING.CARD_PADDING_COMPACT} border border-border rounded-lg transition-all hover:shadow-lg text-left ${action.color}`}
            >
              <div className={`flex items-center ${DASHBOARD_SPACING.ICON_TEXT_GAP} ${DASHBOARD_SPACING.MARGIN_TINY}`}>
                {action.icon}
                <h3 className="font-semibold text-sm">{action.title}</h3>
              </div>
              <p className="text-xs opacity-90 line-clamp-2">
                {action.description}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Content Management Section */}
      <div className={`${CARD_CLASSES} p-6`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Content Management</h2>
              <p className="text-sm text-muted-foreground">
                Manage your company content and resources
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button
              onClick={() => navigate('/content/upload')}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium transition-colors hover:bg-primary/90 flex items-center justify-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Upload Content
            </button>
            <button
              onClick={() => navigate('/content')}
              className="px-4 py-2 bg-card border border-border text-foreground rounded-md text-sm font-medium transition-colors hover:bg-muted flex items-center justify-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Manage All Content
            </button>
          </div>
        </div>

        {/* Content Statistics by Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {contentStats.map((stat, index) => (
            <div key={index} className={`${CARD_CLASSES} bg-muted/30 p-4`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 ${stat.color} rounded-lg`}>
                  {stat.icon}
                </div>
                <div className="flex-1">
                  <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.title}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Content with Quick Actions */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">Recent Content</h3>
          {contentLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className={`${CARD_CLASSES} bg-muted/30 p-4 animate-pulse`}>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-muted rounded" />
                    <div className="flex-1">
                      <div className="h-4 bg-muted rounded w-32 mb-2" />
                      <div className="h-3 bg-muted rounded w-48" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : contentError ? (
            <div className={`${CARD_CLASSES} bg-destructive/10 border-destructive/50 p-4`}>
              <p className="text-sm text-destructive">{contentError}</p>
            </div>
          ) : companyContent.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No content uploaded yet</p>
              <button
                onClick={() => navigate('/content/upload')}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium transition-colors hover:bg-primary/90"
              >
                Upload Your First Content
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {companyContent.slice(0, 5).map((contentItem) => (
                <div
                  key={contentItem.id}
                  className={`${CARD_CLASSES} bg-muted/30 p-4 hover:border-border/80 transition-colors`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {/* Thumbnail or Icon */}
                      {contentItem.thumbnailUrl ? (
                        <img
                          src={contentItem.thumbnailUrl}
                          alt={contentItem.title}
                          className="w-16 h-16 rounded object-cover flex-shrink-0"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-muted rounded flex items-center justify-center flex-shrink-0">
                          <FileText className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}

                      {/* Content Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-foreground mb-1 truncate">{contentItem.title}</h4>
                        <p className="text-sm text-muted-foreground line-clamp-1">{contentItem.description}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs text-muted-foreground">
                            {new Date(contentItem.createdAt).toLocaleDateString()}
                          </span>
                          <span className="text-xs text-muted-foreground">•</span>
                          <span className="text-xs px-2 py-0.5 rounded bg-muted capitalize">
                            {contentItem.type}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <ContentQuickActions
                      content={contentItem}
                      onView={handleViewContent}
                      onEdit={handleEditContent}
                      onDelete={handleDeleteContent}
                      onDownload={handleDownloadContent}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Users */}
      <div className={`${CARD_CLASSES} p-6`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Recent Users</h2>
              <p className="text-sm text-muted-foreground">
                Latest team members to join
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/users')}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium transition-colors hover:bg-primary/90 flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <UserPlus className="w-4 h-4" />
            Manage All Users
          </button>
        </div>

        <div className="space-y-3">
          {recentUsers.map((user) => (
            <div
              key={user.id}
              className={`${CARD_CLASSES} bg-muted/30 p-4 flex items-center justify-between gap-4`}
            >
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-semibold text-primary">
                    {user.name.charAt(0)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground truncate">{user.name}</h4>
                  <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-xs px-2 py-1 rounded-md font-medium capitalize whitespace-nowrap ${getRoleBadgeColor(user.role)}`}>
                  {user.role.replace('_', ' ')}
                </span>
                <span className={`text-xs px-2 py-1 rounded-md font-medium capitalize whitespace-nowrap ${getStatusColor(user.status)}`}>
                  {user.status}
                </span>
                <span className="text-xs text-muted-foreground whitespace-nowrap hidden sm:inline">
                  Joined {user.joined}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Company Analytics Section */}
      <div className={`${CARD_CLASSES} p-6`}>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-primary/10 rounded-lg">
            <BarChart3 className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Company Analytics</h2>
            <p className="text-sm text-muted-foreground">
              Company-wide statistics and insights
            </p>
          </div>
        </div>
        <CompanyAnalytics />
      </div>

      {/* Company Profile Card */}
      <div className={`${CARD_CLASSES} p-6`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Company Profile</h2>
              <p className="text-sm text-muted-foreground">
                Your company information
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/company/profile')}
            className="px-4 py-2 bg-card border border-border text-foreground rounded-md text-sm font-medium transition-colors hover:bg-muted flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <Edit className="w-4 h-4" />
            Edit Profile
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Company Name</label>
              <p className="text-foreground font-medium">{companyName}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Administrator</label>
              <p className="text-foreground font-medium">{userName}</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Total Users</label>
              <p className="text-foreground font-medium">{companyStats?.userCount ?? 0} users</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Content Items</label>
              <p className="text-foreground font-medium">{companyStats?.totalContent ?? 0} items</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
